import { faCube } from "@fortawesome/pro-solid-svg-icons";
import {
	Anchor,
	Button,
	CircleButton,
	ProductionUnit,
	Tooltip,
	VideoPlayerDialog,
	cn,
	formatNumber,
	getPrice,
	separateLocale,
} from "@tosize/ui";
import { FormattedMessage, useIntl } from "react-intl";
import { match } from "ts-pattern";
import { useCardContext } from "../../../context/CardContext";
import { useConfig } from "../../../context/ConfigContext";
import { useErrorContext } from "../../../context/ErrorContext";
import { usePostDataContext } from "../../../context/PostDataContext";
import { usePriceContext } from "../../../context/PriceContext";
import useProducts from "../../../hooks/useProducts";
import { ExplanationButton } from "../../ui/ExplanationButton";

export function Price() {
	const intl = useIntl();
	const { routes } = useConfig();
	const { price, priceType, isCheckModeCompletePanel } = usePriceContext();
	const { hasErrors } = useErrorContext();
	const { productData } = useProducts();
	const { data } = usePostDataContext();

	const video = match(separateLocale(intl.locale).language)
		.with("nl", () => ({ id: "1003530256", time: 363 }))
		.with("de", () => ({ id: "1038150968", time: undefined }))
		.with("fr", () => ({ id: "1039430249", time: undefined }))
		.otherwise(() => undefined);

	return (
		<div className="flex flex-wrap items-start justify-between gap-4 rounded bg-shades-light p-4 xs:px-6 text-sm">
			<div className="flex flex-col gap-2 leading-tight">
				<div>
					<ExplanationButton
						title={`${
							isCheckModeCompletePanel
								? intl.formatMessage({ defaultMessage: "Prijs per hele plaat", id: "iAQu1E" })
								: priceType === "specific"
									? data.unit === ProductionUnit.CompletePanel
										? intl.formatMessage({
												defaultMessage: "Per stuk op maat uit gezaagde hele plaat",
												id: "5MaT/K",
											})
										: intl.formatMessage({
												defaultMessage: "Per stuk op maat gezaagd",
												id: "OmYzV2",
											})
									: priceType === "minimum"
										? intl.formatMessage({
												defaultMessage: "Minimale prijs per stuk",
												id: "HfNUWG",
											})
										: ""
						}: `}
					>
						{!!video && (
							<VideoPlayerDialog
								player={{
									variant: "vimeo",
									id: video.id,
									time: video.time,
									autoPlay: true,
									title: intl.formatMessage({
										defaultMessage: "Configurator Uitleg",
										id: "cxyDG7",
									}),
								}}
								title={intl.formatMessage({
									defaultMessage: "Hulp nodig bij bestellen?",
									id: "4aRzeb",
								})}
							>
								<CircleButton component="button" className="text-sm" />
							</VideoPlayerDialog>
						)}
					</ExplanationButton>
				</div>

				{hasErrors && priceType === "specific" ? (
					<span className="text-base text-primary-medium italic leading-7">
						<FormattedMessage defaultMessage="Vul een geldige afmeting in" id="8g2UgE" />
					</span>
				) : (
					<>
						<div className="flex items-start xs:items-end gap-4 xs:gap-2">
							{productData && productData.product_price_final?.display === 3 ? (
								<Tooltip
									content={
										<div className="flex items-end gap-2 text-sm leading-none">
											<span className="font-bold">
												{formatNumber({
													lang: intl.locale,
													value: getPrice(price, productData.product_price_tax_percentage, true),
													digits: { min: 2 },
												})}
											</span>
											<span>
												<FormattedMessage defaultMessage="inl. BTW" id="BW7Txw" />
											</span>
											<span>-</span>
											<Anchor
												target="_blank"
												href={routes.get("customer_service/delivery/delivery_methods")}
												className="no-touch:text-white"
											>
												{intl.formatMessage({
													defaultMessage: "excl. verzendkosten",
													id: "77aZb9",
												})}
											</Anchor>
										</div>
									}
								>
									<button
										type="button"
										className="appearance-none text-success-medium text-xl leading-none"
									>
										{formatNumber({ lang: intl.locale, value: price, digits: { min: 2 } })}
									</button>
								</Tooltip>
							) : (
								<span className="text-success-medium text-xl leading-none">
									{formatNumber({ lang: intl.locale, value: price, digits: { min: 2 } })}
								</span>
							)}

							<div className="flex xs:flex-row flex-col gap-1">
								<div className="text-shades-dark text-sm leading-none">
									{productData?.product_price_final?.display !== 2 ? (
										<FormattedMessage defaultMessage="excl. BTW" id="iZDVbs" />
									) : (
										<FormattedMessage defaultMessage="incl. BTW" id="kmeA4I" />
									)}
								</div>

								<span className="xs:inline hidden leading-none">-</span>

								<Anchor
									target="_blank"
									href={routes.get("customer_service/delivery/delivery_methods")}
									className="text-shades-dark text-sm leading-none"
								>
									{intl.formatMessage({ defaultMessage: "excl. verzendkosten", id: "77aZb9" })}
								</Anchor>
							</div>
						</div>
					</>
				)}
			</div>

			<Visualisation />
		</div>
	);
}

function Visualisation() {
	const intl = useIntl();
	const { showVisualisation, setShowVisualisation } = useCardContext();
	const { mode } = useConfig();

	if (showVisualisation) {
		return null;
	}

	return (
		<Button
			className={cn("hidden", { "lg:inline": mode === "add" })}
			variant="link"
			starticon={faCube}
			onClick={() => setShowVisualisation((prev) => !prev)}
		>
			{intl.formatMessage({ defaultMessage: "Bekijk preview", id: "0Cw2m8" })}
		</Button>
	);
}
